import { HTMLText, Text } from 'pixi.js';
import type { Container } from 'pixi.js';
import type { VariableBank, VariableValue } from './VariableBank';
import { setTextAsTimerCountdown } from '../../utils/pixi/setTextAsTimerCountdown';
import { getAllDisplayObjectsFrom } from '../../utils/pixi/getAllDisplayObjectsFrom';

// mixes layout logic with text
function extractBetweenAngles(str: string): string[] {
  return [...str.matchAll(/<([^>]+)>/g)].map((m) => m[1]);
}

/* eslint-disable @typescript-eslint/no-extraneous-class */
export class VariableBankUtils {
  public static applyVariableValueToText(textObject: Text | HTMLText, variable: VariableValue): void {
    if (variable.type === 'text') {
      textObject.text = textObject.text.replaceAll(`<${variable.name}>`, variable.value);
    } else if (variable.type === 'time') {
      // dont make timer override whole text
      // store text and variable
      setTextAsTimerCountdown(textObject, variable.value);
    } else if (variable.type === 'displayObject') {
      textObject.visible = false;
      textObject.renderable = false;
      variable.value().props({
        parent: textObject.parent,
        x: textObject.x,
        y: textObject.y,
      });
    }
  }

  public static hookBankToView(bank: VariableBank, root: Container): void {
    const refreshVariables = (): void => {
      const texts = getAllDisplayObjectsFrom(root).filter(
        (obj: unknown): obj is Text | HTMLText => obj instanceof Text || obj instanceof HTMLText,
      );
      texts.forEach((text) => {
        this.applyVariablesToText(bank, text);
      });
    };

    refreshVariables();
    bank.onVariableSet.connect(refreshVariables);
  }

  public static applyVariablesToText(bank: VariableBank, textObject: Text | HTMLText): void {
    if (textObject.destroyed) return;

    const missedVariableNames = new Set<string>();

    extractBetweenAngles(textObject.text).forEach((variableName) => {
      const variable = bank.getVariable(variableName);

      if (!variable) {
        missedVariableNames.add(variableName);
        return;
      }

      this.applyVariableValueToText(textObject, variable);
    });

    if (missedVariableNames.size > 0) {
      const connection = bank.onVariableSet.connect((newVariable) => {
        if (!missedVariableNames.has(newVariable.name)) return;
        this.applyVariableValueToText(textObject, newVariable);
        missedVariableNames.delete(newVariable.name);
        if (missedVariableNames.size === 0) connection.disconnect();
      });

      textObject.once('destroyed', () => connection.disconnect());
    }
  }
}
